/**
 * Customer Profile Storage Utility
 * Stores customer profile and authentication token in localStorage with expiration
 */

export interface StoredCustomerProfile {
  customer: {
    id: string
    firstName: string
    lastName: string
    email?: string
    phone?: string
  }
  token: string
  expiresAt: number // timestamp in milliseconds
  formToken: string // the form token they completed
}

const STORAGE_PREFIX = 'customerFormProfile_'
const EXPIRATION_DAYS = 30

/**
 * Get storage key for a specific form token
 */
const getStorageKey = (formToken: string): string => {
  return `${STORAGE_PREFIX}${formToken}`
}

/**
 * Store customer profile with 30-day expiration
 */
export const storeCustomerProfile = (
  formToken: string,
  customer: StoredCustomerProfile['customer'],
  token: string
): void => {
  try {
    const expiresAt = Date.now() + (EXPIRATION_DAYS * 24 * 60 * 60 * 1000)
    
    const profile: StoredCustomerProfile = {
      customer,
      token,
      expiresAt,
      formToken,
    }
    
    const storageKey = getStorageKey(formToken)
    localStorage.setItem(storageKey, JSON.stringify(profile))
    
    console.log('✅ Customer profile stored for form:', formToken, 'Expires:', new Date(expiresAt))
  } catch (error) {
    console.error('❌ Failed to store customer profile:', error)
  }
}

/**
 * Retrieve customer profile if valid and not expired
 */
export const getCustomerProfile = (formToken: string): StoredCustomerProfile | null => {
  try {
    const storageKey = getStorageKey(formToken)
    const stored = localStorage.getItem(storageKey)
    
    if (!stored) {
      return null
    }
    
    const profile: StoredCustomerProfile = JSON.parse(stored)
    
    // Check if expired
    if (Date.now() > profile.expiresAt) {
      console.log('⚠️ Stored customer profile expired, clearing...')
      clearCustomerProfile(formToken)
      return null
    }
    
    // Verify form token matches
    if (profile.formToken !== formToken) {
      console.warn('⚠️ Form token mismatch, clearing profile...')
      clearCustomerProfile(formToken)
      return null
    }
    
    console.log('✅ Retrieved valid customer profile for form:', formToken)
    return profile
  } catch (error) {
    console.error('❌ Failed to retrieve customer profile:', error)
    // Clear corrupted data
    clearCustomerProfile(formToken)
    return null
  }
}

/**
 * Clear customer profile for a specific form
 */
export const clearCustomerProfile = (formToken: string): void => {
  try {
    const storageKey = getStorageKey(formToken)
    localStorage.removeItem(storageKey)
    console.log('✅ Cleared customer profile for form:', formToken)
  } catch (error) {
    console.error('❌ Failed to clear customer profile:', error)
  }
}

/**
 * Clear all expired customer profiles
 */
export const clearExpiredProfiles = (): void => {
  try {
    const keysToRemove: string[] = []
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const profile: StoredCustomerProfile = JSON.parse(stored)
            if (Date.now() > profile.expiresAt) {
              keysToRemove.push(key)
            }
          }
        } catch (error) {
          // Corrupted data, remove it
          keysToRemove.push(key)
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key))
    
    if (keysToRemove.length > 0) {
      console.log(`✅ Cleared ${keysToRemove.length} expired customer profiles`)
    }
  } catch (error) {
    console.error('❌ Failed to clear expired profiles:', error)
  }
}

/**
 * Check if a customer profile exists and is valid
 */
export const hasValidCustomerProfile = (formToken: string): boolean => {
  return getCustomerProfile(formToken) !== null
}

